import { mat4 } from 'gl-matrix';

/**
 * Decompose a 4x4 rigid transformation matrix into Euler angles (Z-Y-X convention)
 * and a translation vector: the matrix is the translation, then rotations
 * about Z, Y and X, applied to a point in that reverse order.
 *
 * gl-matrix stores matrices in column-major order:
 *   [m0  m4  m8  m12]
 *   [m1  m5  m9  m13]
 *   [m2  m6  m10 m14]
 *   [m3  m7  m11 m15]
 */
export function decomposeRigidTransform(m: mat4): {
    translationMm: [number, number, number];
    eulerAnglesDeg: [number, number, number];
} {
    const translationMm: [number, number, number] = [m[12], m[13], m[14]];

    // R = Rz * Ry * Rx, and R[row][col] is m[col * 4 + row]: R20 = -sin(ry)
    // is m[2], R21 and R22 are m[6] and m[10], R00 and R10 are m[0] and m[1].
    const sy = -m[2];
    const cy = Math.sqrt(m[0] * m[0] + m[1] * m[1]);

    let rx: number;
    let ry: number;
    let rz: number;

    if (cy > 1e-6) {
        rx = Math.atan2(m[6], m[10]);
        ry = Math.atan2(sy, cy);
        rz = Math.atan2(m[1], m[0]);
    } else {
        // At ry = ±90° only rx ± rz is defined: rz is taken as 0, and rx
        // comes from R12 (m[9]) and R11 (m[5]).
        rx = Math.atan2(-m[9], m[5]);
        ry = Math.atan2(sy, cy);
        rz = 0;
    }

    const toDeg = 180 / Math.PI;
    const eulerAnglesDeg: [number, number, number] = [
        rx * toDeg,
        ry * toDeg,
        rz * toDeg,
    ];

    return { translationMm, eulerAnglesDeg };
}
